import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { ApiSliceType, PublicApiSliceType } from "../../../../types/interfaces";
import { TagTypes } from "../../../apiSlice";
import { getCommonInvalidationTags } from "./moduleApiUtils";

/**
 * Creates query endpoints for getting module data and defaults
 */
export function createModuleGetApi<MF>({
  apiSlice,
  path,
  moduleName,
  transformResponse,
  tag,
  tagDefaults
}: {
  apiSlice: ApiSliceType | PublicApiSliceType;
  path: string;
  moduleName: string;
  transformResponse?: (response: any) => MF | null;
  tag: TagTypes,
  tagDefaults?: TagTypes
}) {
  const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
    [`get${moduleName}`]: builder.query<MF | null, number>({
      query: (id) => `${path}/${id}/`,
      transformResponse,
      providesTags: (_results, _error, id) => [{ type: tag, id }]
    }),
    [`get${moduleName}Defaults`]: builder.query<MF | null, number>({
      query: (id) => `${path}/${id}/defaults/`,
      providesTags: (_results, _error, id) => tagDefaults ? [{ type: tagDefaults, id }] : []
    })
  });

  return apiSlice.injectEndpoints({
    endpoints,
    overrideExisting: true
  });
}

/**
 * Creates mutation endpoint for updating module data
 */
export function createModuleUpdateApi<MF, RES>({
  apiSlice,
  path,
  moduleName,
  transformUpdatePayload,
  tag,
  tagDefaults,
  includesLandUseChange
}: {
  apiSlice: ApiSliceType | PublicApiSliceType;
  path: string;
  moduleName: string;
  transformUpdatePayload: (data: Partial<MF>, activityId: number) => { body: any, id?: number };
  tag: TagTypes,
  tagDefaults?: TagTypes,
  includesLandUseChange?: boolean
}) {
  type PayloadType = {
    data: Partial<MF>,
    activityId: number
  }

  const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
    [`update${moduleName}`]: builder.mutation<RES, PayloadType>({
      query({ data, activityId }) {
        const { body, id } = transformUpdatePayload(data, activityId);
        return {
          url: `${path}/${id}/`,
          method: "PATCH",
          body
        };
      },
      invalidatesTags: (results: any, _error, { activityId }) => getCommonInvalidationTags({
        results,
        moduleId: results?.id,
        activityId,
        moduleTag: tag,
        endpoint: path,
        includesLandUseChange,
        tagDefaults
      })
    })
  });
  return apiSlice.injectEndpoints({
    endpoints,
    overrideExisting: true
  });
}

export function createEntriesGetApi<MF>({
  apiSlice,
  path,
  moduleName,
  transformResponse,
  tag,
  tagDefaults
}: {
  apiSlice: ApiSliceType | PublicApiSliceType;
  path: string;
  moduleName: string;
  transformResponse?: (response: any) => MF | null;
  tag: TagTypes,
  tagDefaults?: TagTypes
}) {
  const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
    [`get${moduleName}`]: builder.query<MF | null, { activityId: number }>({
      query: ({ activityId }) => `${path}/?activity=${activityId}`,
      transformResponse,
      providesTags: (_results, _error, { activityId }) => [{ type: tag, id: activityId }]
    }),
    [`get${moduleName}Defaults`]: builder.query<MF | null, number>({
      query: (id) => `${path}/${id}/defaults/`,
      providesTags: (_results, _error, id) => tagDefaults ? [{ type: tagDefaults, id }] : []
    }),
  });

  return apiSlice.injectEndpoints({
    endpoints,
    overrideExisting: true
  });
}